import { Button, Divider, Flex, Grid, Popover, Progress, Switch } from 'antd';
import dayjs from 'dayjs';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { TextEllipsis, TextTertiary } from '../text';

import { dateRangePresets } from './constants';

import type { Dayjs } from 'dayjs';

export type DateRangeFilterProps = {
  presetKey?: string;
  from: Dayjs;
  to: Dayjs;
  compare?: boolean;
  onPresetChange: (presetKey: string) => void;
  onRangeChange: (from: Dayjs, to: Dayjs) => void;
  onCompareChange?: (compare: boolean) => void;
};

export const DateRangeFilter = ({
  presetKey,
  from,
  to,
  compare = false,
  onPresetChange,
  onRangeChange,
  onCompareChange,
}: DateRangeFilterProps) => {
  const isMobile = Grid.useBreakpoint().md === false;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [popupLayout, setPopupLayout] = useState({ width: 760, offsetX: 0 });
  const [visibleMonth, setVisibleMonth] = useState(to.startOf('month'));
  const [pickedStart, setPickedStart] = useState<Dayjs | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Dayjs | null>(null);

  const today = dayjs().startOf('day');
  const previewEnd = hoveredDate ?? pickedStart;
  const [rangeFrom, rangeTo] = pickedStart
    ? previewEnd && previewEnd.isBefore(pickedStart)
      ? [previewEnd, pickedStart]
      : [pickedStart, previewEnd ?? pickedStart]
    : [from, to];
  const days = to.diff(from, 'day') + 1;
  const comparedFrom = from.subtract(days, 'day');
  const comparedTo = to.subtract(days, 'day');
  const formatDate = (date: Dayjs) =>
    date.format(date.isSame(today, 'year') ? 'MMM D' : 'MMM D, YYYY');
  const formatRange = (start: Dayjs, end: Dayjs) =>
    start.isSame(end, 'day') ? formatDate(start) : `${formatDate(start)} – ${formatDate(end)}`;
  const daysLabel = days === 1 ? '1 day' : `${days} days`;

  const measurePopupLayout = () => {
    const viewportWidth = document.documentElement.clientWidth;
    const triggerRight = triggerRef.current?.getBoundingClientRect().right ?? viewportWidth;
    const width = Math.min(760, viewportWidth - 24);
    setPopupLayout({ width, offsetX: Math.max(0, 12 + width - triggerRight) });
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    window.addEventListener('resize', measurePopupLayout);
    return () => window.removeEventListener('resize', measurePopupLayout);
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      measurePopupLayout();
    }
    setOpen(nextOpen);
    setPickedStart(null);
    setHoveredDate(null);
    setVisibleMonth(to.startOf('month'));
  };

  const panel = (
    <Flex vertical style={{ width: popupLayout.width }}>
      <Flex vertical={isMobile}>
        <Flex
          vertical={!isMobile}
          wrap={isMobile}
          justify="space-between"
          gap={4}
          style={
            isMobile
              ? { padding: '12px 12px 8px', borderBottom: '1px solid var(--ant-color-split)' }
              : {
                  width: 160,
                  padding: '16px 10px',
                  borderRight: '1px solid var(--ant-color-split)',
                }
          }
        >
          {dateRangePresets.map((preset) => (
            <Button
              key={preset.key}
              data-testid={`date-range-preset-${preset.key}`}
              type="text"
              icon={preset.icon}
              onClick={() => {
                setPickedStart(null);
                setVisibleMonth(preset.range()[1].startOf('month'));
                onPresetChange(preset.key);
              }}
              style={{
                justifyContent: 'flex-start',
                gap: 10,
                paddingInline: 12,
                fontWeight: preset.key === presetKey ? 600 : 500,
                backgroundColor:
                  preset.key === presetKey ? 'var(--ant-color-fill-tertiary)' : undefined,
              }}
            >
              {preset.menuLabel}
            </Button>
          ))}
        </Flex>

        <Flex vertical flex={1} style={{ padding: isMobile ? '12px 12px 10px' : '16px 18px 14px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              gap: 24,
            }}
          >
            {(isMobile ? [visibleMonth] : [visibleMonth.subtract(1, 'month'), visibleMonth]).map(
              (month, monthIndex, months) => (
                <Flex vertical key={month.format('YYYY-MM')}>
                  <Flex align="center" justify="space-between" style={{ paddingBottom: 10 }}>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<ChevronLeft size={16} />}
                      onClick={() => setVisibleMonth(visibleMonth.subtract(1, 'month'))}
                      style={{
                        visibility: monthIndex === 0 ? undefined : 'hidden',
                        color: 'var(--ant-color-text-tertiary)',
                      }}
                    />
                    <TextEllipsis style={{ fontSize: 14, fontWeight: 600 }}>
                      {month.format('MMMM YYYY')}
                    </TextEllipsis>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<ChevronRight size={16} />}
                      onClick={() => setVisibleMonth(visibleMonth.add(1, 'month'))}
                      style={{
                        visibility: monthIndex === months.length - 1 ? undefined : 'hidden',
                        color: 'var(--ant-color-text-tertiary)',
                      }}
                    />
                  </Flex>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: 2,
                      paddingBottom: 4,
                    }}
                  >
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((weekday) => (
                      <TextTertiary
                        key={weekday}
                        style={{ height: 22, textAlign: 'center', fontSize: 11, fontWeight: 500 }}
                      >
                        {weekday}
                      </TextTertiary>
                    ))}
                  </div>
                  <div
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}
                    onMouseLeave={() => setHoveredDate(null)}
                  >
                    {Array.from({ length: month.day() }, (_, index) => (
                      <span key={`blank-${index}`} style={{ height: 34 }} />
                    ))}
                    {Array.from({ length: month.daysInMonth() }, (_, index) => {
                      const date = month.date(index + 1);
                      const isEdge = date.isSame(rangeFrom, 'day') || date.isSame(rangeTo, 'day');
                      return (
                        <Button
                          key={index}
                          type={isEdge ? 'primary' : 'text'}
                          disabled={date.isAfter(today, 'day')}
                          onMouseEnter={() => (pickedStart ? setHoveredDate(date) : undefined)}
                          onClick={() => {
                            if (!pickedStart) {
                              setPickedStart(date);
                              return;
                            }
                            setPickedStart(null);
                            setHoveredDate(null);
                            onRangeChange(
                              date.isBefore(pickedStart) ? date : pickedStart,
                              date.isBefore(pickedStart) ? pickedStart : date,
                            );
                          }}
                          style={{
                            height: 34,
                            padding: 0,
                            fontSize: 13,
                            fontVariantNumeric: 'tabular-nums',
                            color:
                              isEdge || date.isAfter(today, 'day')
                                ? undefined
                                : date.isSame(today, 'day')
                                  ? 'var(--ant-color-primary)'
                                  : 'var(--ant-color-text)',
                            backgroundColor:
                              date.isAfter(rangeFrom, 'day') && date.isBefore(rangeTo, 'day')
                                ? 'var(--ant-color-fill-secondary)'
                                : undefined,
                          }}
                        >
                          {date.date()}
                        </Button>
                      );
                    })}
                  </div>
                </Flex>
              ),
            )}
          </div>

          {pickedStart ? (
            <TextTertiary style={{ marginTop: 14, fontSize: 12 }}>
              Pick the end of the range on the calendar.
            </TextTertiary>
          ) : compare ? (
            <>
              <Divider style={{ margin: '14px 0 12px' }} />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 8,
                }}
              >
                {[
                  {
                    caption: `Compared to · ${daysLabel}`,
                    range: formatRange(comparedFrom, comparedTo),
                    color: 'var(--ant-orange-5)',
                    fontWeight: 500,
                  },
                  {
                    caption: `Selected · ${daysLabel}`,
                    range: formatRange(from, to),
                    color: 'var(--ant-green-7)',
                    fontWeight: 600,
                  },
                ].map((period) => (
                  <Flex vertical gap={6} key={period.caption}>
                    <Progress
                      percent={100}
                      showInfo={false}
                      strokeColor={period.color}
                      size={{ height: 6 }}
                    />
                    <TextTertiary style={{ fontSize: 11 }}>{period.caption}</TextTertiary>
                    <TextEllipsis style={{ fontSize: 13, fontWeight: period.fontWeight }}>
                      {period.range}
                    </TextEllipsis>
                  </Flex>
                ))}
              </div>
            </>
          ) : null}
        </Flex>
      </Flex>

      {onCompareChange && (
        <Flex
          align="center"
          gap={12}
          onClick={() => onCompareChange(!compare)}
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--ant-color-split)',
            cursor: 'pointer',
          }}
        >
          <Flex vertical flex={1}>
            <TextEllipsis style={{ fontSize: 13, fontWeight: 500 }}>
              Compare to previous period
            </TextEllipsis>
            <TextEllipsis style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
              {compare
                ? `${formatRange(comparedFrom, comparedTo)} · matched automatically to your range`
                : 'Off — tiles show the current period only'}
            </TextEllipsis>
          </Flex>
          <Switch
            size="small"
            checked={compare}
            style={{ backgroundColor: compare ? 'var(--ant-color-text)' : undefined }}
          />
        </Flex>
      )}
    </Flex>
  );

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomRight"
      autoAdjustOverflow={false}
      align={{ offset: [popupLayout.offsetX, 4] }}
      styles={{
        container: { padding: 0, borderRadius: 20, overflow: 'hidden' },
      }}
      content={panel}
    >
      <Button
        ref={triggerRef}
        data-testid="date-range-filter"
        icon={<CalendarDays size={15} />}
        style={{
          gap: 7,
          paddingInline: 14,
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--ant-color-text-secondary)',
        }}
      >
        {dateRangePresets.find(({ key }) => key === presetKey)?.label ?? formatRange(from, to)}
        <ChevronDown size={12} color="var(--ant-color-text-tertiary)" />
      </Button>
    </Popover>
  );
};
